import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";

import { useDispatch } from "react-redux";
import { deleteFile, getAllFiles, getFilesByLead, uploadFile } from "../../../../../store/slices/FileDataSlice";
import { isAdminAuthLevel } from "../../../../../utils/opsLeadAttachments";


const FileUploadFollowupTable = ({ LeadReport, setShowModal, isViewOnly = false }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const normalizeCategory = (v) => String(v || "").trim().toLowerCase();

  // Follow-up table rows can contain different identifiers depending on API/source.
  // To "hard solve" empty attachments on localhost, we try multiple candidate ids
  // and use the first one that returns files.
  const leadIdCandidates = Array.from(
    new Set(
      [LeadReport?.id, LeadReport?._id, LeadReport?.leadId]
        .map((v) => (v == null ? "" : String(v).trim()))
        .filter(Boolean)
    )
  );
  const primaryLeadId = leadIdCandidates[0] || "";
  const [SelectedCategory, setSelectedCategory] = useState("");

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [editfetchedfiles, seteditFetchedFiles] = useState({ payload: [] });
      const [fileNames, setFileNames] = useState([]);

  const auth = localStorage.getItem("auth");
  const normalizedAuth = String(auth || "").trim().toUpperCase();
  const isFinanceUser = normalizedAuth === "L2";
  const isAccountingUser = normalizedAuth === "L5";
  const isDirector = normalizedAuth === "L1";
  const isSalesUser = normalizedAuth === "L3";
  const isAdminUser = isAdminAuthLevel(auth);

  const allowedCategoriesForUser = isDirector
    ? ["Quote", "Purchase Order", "Contract"]
    : isFinanceUser || isAccountingUser
      ? ["Purchase Order", "Contract"]
      : isSalesUser
        ? ["Quote", "Purchase Order", "Contract", "Communication", "Miscellaneous"]
        : ["Communication", "Miscellaneous"];

  useEffect(() => {
    if (isAdminUser) return;
    const fetchFiles = async () => {
      try {
        if (leadIdCandidates.length === 0) {
          seteditFetchedFiles({ payload: [] });
          return;
        }
        const tryGetList = async (candidateId) => {
          const res = await dispatch(getFilesByLead(candidateId)).unwrap();
          if (Array.isArray(res)) return res;
          if (Array.isArray(res?.payload)) return res.payload;
          if (Array.isArray(res?.files)) return res.files;
          return [];
        };

        let list = [];
        for (const candidateId of leadIdCandidates) {
          // eslint-disable-next-line no-await-in-loop
          list = await tryGetList(candidateId);
          if (Array.isArray(list) && list.length > 0) break;
        }

        // Fallback: if per-lead endpoint returns empty (common backend inconsistency),
        // pull all files once and filter locally by any candidate id.
        if (!Array.isArray(list) || list.length === 0) {
          const all = await dispatch(getAllFiles()).unwrap();
          const allList = Array.isArray(all)
            ? all
            : Array.isArray(all?.payload)
              ? all.payload
              : Array.isArray(all?.files)
                ? all.files
                : [];
          const candidates = new Set(leadIdCandidates.map((x) => String(x)));
          list = allList.filter((f) => candidates.has(String(f?.leadId ?? f?.leadid ?? "")));
        }

        // De-dupe defensively (some backends may return duplicates)
        const seen = new Set();
        const deduped = (Array.isArray(list) ? list : []).filter((f) => {
          const key =
            String(f?.id || f?._id || "") +
            "|" +
            String(f?.filepath || f?.filepathurl || f?.filename || f?.name || "");
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        seteditFetchedFiles({ payload: deduped });
      } catch (err) {
        console.error("Error fetching files:", err);
      }
    };

    fetchFiles();
    // We intentionally key this effect off `primaryLeadId` to avoid noisy re-fetches
    // from array identity changes while still updating when the lead changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAdminUser, primaryLeadId]);

  const dropdownRefProbability = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileDownload = (fileData) => {
    if (isViewOnly) return;
    try {
      if (!fileData || !fileData.filepathurl) {
        throw new Error("Invalid file data");
      }

      const fileUrl = fileData.filepathurl;
      const fileName = fileData.filename || fileUrl.split("/").pop();

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar("File download started", { variant: "success" });
    } catch (error) {
      console.error("Download failed:", error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  };

  const handleFileChange = (e) => {
    if (isViewOnly) return;
    const selectedFiles = Array.from(e.target.files);

    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!SelectedCategory) {
      enqueueSnackbar("Please select a category first", {
        variant: "error",
      });
      return;
    }

    setFileNames((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const validFiles = [];

      selectedFiles.forEach((file) => {
        if (!allowedFormats.includes(file.type)) {
          enqueueSnackbar(`Only PDF, DOC, DOCX, XLS, and XLSX files are allowed (${file.name})`, {
            variant: "error",
          });
          return;
        }

        const isDuplicate = [
          ...existing,
          ...(editfetchedfiles?.payload || [])
        ].some(
          (f) => f.name === file.name ||
            (f.filepath && f.filepath.split('/').pop() === file.name)
        );

        if (isDuplicate) {
          enqueueSnackbar(`File "${file.name}" is already uploaded`, {
            variant: "error",
          });
          return;
        }

        const fileWithCategory = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        fileWithCategory.selectedCategory = SelectedCategory;
        validFiles.push(fileWithCategory);
      });

      if (validFiles.length === 0) return prev;

      seteditFetchedFiles((prevFetched) => {
        const currentFiles = prevFetched?.payload || [];

        const newFiles = validFiles.map((file) => ({
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filepath: `uploads/filesdata/${file.name}`,
          filepathurl: URL.createObjectURL(file),
          leadId: primaryLeadId,
          selectedCategory: file.selectedCategory,
          isNew: true,
          name: file.name,
          type: file.type
        }));

        handleFileUpload(primaryLeadId, newFiles);


        return {
          ...prevFetched,
          payload: [...currentFiles, ...newFiles]
        };

      });

      return [...existing, ...validFiles];
    });

    setSelectedCategory("");
    e.target.value = null;
  };

  // const handleFileUpload = useCallback(
  //   async (id, newFiles) => {
  //     if (!newFiles || newFiles.length === 0) return;

  //     const uploadDate = new Date().toISOString().slice(0, 10);
  //     const formData = new FormData();
  //     formData.append("leadId", LeadReport._id);
  //     formData.append("uploadDate", uploadDate);

  //     newFiles.forEach((file) => {
  //       formData.append("selectedCategory", file.selectedCategory);
  //       formData.append("files", file);
  //     });

  //     try {
  //       await dispatch(uploadFile(formData)).unwrap();
  //       dispatch(fetchFilesByLeadId(id));
  //       enqueueSnackbar("File uploaded successfully", { variant: "success" });
  //     } catch (error) {
  //       enqueueSnackbar("Error occurred while uploading file", {
  //         variant: "error",
  //       });
  //       console.error(
  //         "Error uploading file:",
  //         error.response?.data?.error || error.message
  //       );
  //     }
  //   },
  //   [dispatch,LeadReport, enqueueSnackbar]
  // );

  const handleFileUpload = useCallback(
    async (id, newFiles) => {
      if (!newFiles || newFiles.length === 0) return;

      const uploadDate = new Date().toISOString().slice(0, 10);
      const formData = new FormData();
      formData.append("leadId", primaryLeadId);
      formData.append("uploadDate", uploadDate);

      try {
        // For each file, we need to fetch the actual file data from the blob URL
        for (const fileObj of newFiles) {
          // Fetch the file data from the blob URL
          const response = await fetch(fileObj.filepathurl);
          const blob = await response.blob();

          // Create a new File object from the blob
          const file = new File([blob], fileObj.name, { type: fileObj.type });

          // Append to FormData
          formData.append("selectedCategory", fileObj.selectedCategory);
          formData.append("files", file);
        }

        await dispatch(uploadFile(formData)).unwrap();
        dispatch(getFilesByLead(id));
        enqueueSnackbar("File uploaded successfully", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Error occurred while uploading file", {
          variant: "error",
        });
        console.error(
          "Error uploading file:",
          error.response?.data?.error || error.message
        );
      }
    },
    [dispatch, enqueueSnackbar, primaryLeadId]
  );

  const handleFileDelete = async (file) => {
    if (isViewOnly) return;
    const id = file.id;

    try {
      await dispatch(deleteFile(id)).unwrap();

      // Update state by removing the deleted file
      seteditFetchedFiles(prev => ({
        ...prev,
        payload: prev.payload.filter(f => f.id !== id)
      }));

      enqueueSnackbar("File deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error occurred while deleting file", { variant: "error" });
      console.error("Error deleting file:", error);
    }
  };

  // Get the files to display - always use the payload array
  const filesToDisplay = editfetchedfiles?.payload || [];
  const visibleFiles =
    isDirector || isFinanceUser || isAccountingUser
      ? filesToDisplay.filter((file) =>
          allowedCategoriesForUser
            .map(normalizeCategory)
            .includes(normalizeCategory(file.category || file.selectedCategory))
        )
      : filesToDisplay;


  return (
    isAdminUser ? null : (
    <div>
      <div className="fixed w-full inset-0 bg-black/50 flex items-center justify-center z-[999]">
        <div className="w-[50%] bg-white shadow-lg rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex align-middle items-center gap-2">
              <h2 className="text-lg font-bold">Attachments </h2>
              <p>({"Project Name : "}{LeadReport.ProjectName})</p>
            </div>            <button
              className="text-gray-500 hover:text-black"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </div>
          <div className="flex gap-2">
            <div className="relative bg-white">
              <div
                ref={dropdownRefProbability}
                className=" flex  p-0  rounded-xl align-middle text-[12px]"
              >
                <button
                  type="button"
                  disabled={isViewOnly}
                  onClick={() => {
                    if (isViewOnly) return;
                    setShowCategoryDropdown(!showCategoryDropdown);
                  }}
                  className={`border relative w-24 h-8 border-gray-400 text-nowrap rounded-md p-2 shadow-md overflow-hidden outline-none flex items-center justify-between bg-gray-200 ${isViewOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <p className="text-left text-xs truncate">
                    {SelectedCategory ? SelectedCategory : "Category"}
                  </p>
                  <ExpandMoreIcon
                    color="white"
                    className="cursor-pointer bg-gray-200 flex-shrink-0"
                  />
                </button>

                {!isViewOnly && showCategoryDropdown && (
                  <div
                    style={{ zIndex: 9999 }}
                    className="absolute top-full left-0 w-40 p-1 cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                  >
                    {allowedCategoriesForUser.map((category) => (
                      <div
                        key={category}
                        className="p-1 hover:bg-gray-200 cursor-pointer"
                        onClick={() => {
                          setSelectedCategory(category);
                          setShowCategoryDropdown(!showCategoryDropdown);
                        }}
                      >
                        {category}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <label
                className={`text-xs p-2 w-24 h-8 text-center rounded-md flex items-center justify-center ${isViewOnly
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed opacity-50 pointer-events-none"
                  : !SelectedCategory
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-gray-800 text-white hover:text-black hover:bg-gray-300 cursor-pointer"
                  }`}
              >
                <span>Upload File</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileChange(e)}
                  disabled={isViewOnly || !SelectedCategory}
                />
              </label>

              <p className="truncate text-xs pl-1">
                {fileNames?.name || "No file selected"}
              </p>
              <div className="flex gap-2 text-red-500 text-[12px] pl-3">
                <p> only .pdf, .xlsx and .docx allowed</p>
              </div>
            </div>
          </div>
          <div className="w-full mt-4 text-left border-collapse text-[13px]">
            <div>
              <div className="border-b flex w-full">
                <div className="py-2 px-4 w-[15%]">Sr. No.</div>
                <div className="py-2 px-4 w-[40%]">File Name</div>
                <div className="py-2 px-4 text-center w-[15%]">Category</div>
                <div className="py-2 px-4 text-center w-[15%]">Download</div>
                <div className="py-2 px-4 text-center w-[15%]">Delete</div>
              </div>
            </div>
            <div>
              {visibleFiles.map((file, index) => (
                <div className="border-b flex w-full" key={file.id ?? index}>
                  <div className="py-2 px-4 w-[15%]">{index + 1}</div>
                  <div className="py-2 px-4 w-[40%] truncate overflow-hidden">{file.filename || file.name}</div>
                  <div className="py-2 px-4 w-[15%] text-center truncate">
                    {file.category || file.selectedCategory}
                  </div>
                  <div className="py-2 px-4 text-center w-[15%]">
                    <button
                      onClick={() => { if (!isViewOnly) handleFileDownload(file); }}
                      disabled={isViewOnly}
                      className={`text-white px-2 py-1 rounded ${isViewOnly ? "bg-blue-500 opacity-50 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-700"}`}
                    >
                      <CloudDownloadIcon />
                    </button>
                  </div>
                  <div className="py-2 px-4 text-center w-[15%]">
                    <button
                      onClick={() => { if (!isViewOnly) handleFileDelete(file); }}
                      disabled={isViewOnly}
                      className={`text-white px-2 py-2 rounded ${isViewOnly ? "bg-red-500 opacity-50 cursor-not-allowed" : "bg-red-500 hover:bg-red-700"}`}
                    >
                      <FaTrash size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    )
  );
}

export default FileUploadFollowupTable